import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DictionaryHeader, DictionaryList, Pagination, Search } from 'src/containers';
import { useLanguagePairContext } from 'src/context/languagePair';
import { useMediaQuerySSR } from 'src/hooks/useStateSSR';
import theme from 'src/utils/theme';
import { useIntl } from 'react-intl';
import { useTargetMessages } from 'src/hooks/useTargetMessages';
import { useRouter } from 'next/router';
import { ROUTES } from 'src/constants/routes.const';
import { getHref } from 'src/utils/getHref';
import { useDebounce } from 'src/hooks/useDebounce';
import { useEventPhrasebookPageSearch } from 'src/analytics/useEventPhrasebookPageSearch';
import { PhrasebookPageProps } from '../types/phrasebookPageProps';
import * as S from './styles';
import PhrasebookPageHeader from './PhrasebookPageHeader';
import * as msgsData from './messages';

const { msgs, ...localesWithMessages } = msgsData;

export const PAGE_SIZE = 25;

type Props = Pick<PhrasebookPageProps, 'learnables' | 'page' | 'search' | 'totalCount'>;

export const PhrasebookPageContent = ({ learnables, page, search, totalCount }: Props) => {
  const intl = useIntl();
  const targetMsgs = useTargetMessages(localesWithMessages);
  const router = useRouter();
  const isMobile = useMediaQuerySSR(false, { maxWidth: theme.sz.md });
  const searchData = useMemo(
    () => ({
      query: search,
      resultsLength: totalCount,
    }),
    [search, totalCount],
  );
  const eventPhrasebookPageSearch = useEventPhrasebookPageSearch(searchData);

  const {
    sourceLanguageLocale,
    sourceLanguageName,
    sourceLanguageImage,
    targetLanguageLocale,
    targetLanguageName,
    targetLanguageImage,
  } = useLanguagePairContext();

  const searchRef = useRef<HTMLInputElement | null>(null);
  const previousSearchValueRef = useRef<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const changePage = useCallback(
    /* istanbul ignore next: tested manually */
    (page: number) => {
      setIsLoading(true);

      const url = getHref({
        pathname: ROUTES.phrasebook.allLearnables,
        query: {
          sourceLanguageLocale,
          targetLanguageLocale,
          search,
          page,
        },
      });

      void router.push(url);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, sourceLanguageLocale, targetLanguageLocale],
  );

  const changeSearch = useCallback(
    /* istanbul ignore next: tested manually */
    (event: Event) => {
      setIsLoading(true);

      previousSearchValueRef.current = search;

      const newSearch = (event.target as HTMLInputElement).value.trimStart();

      const url = getHref({
        pathname: ROUTES.phrasebook.allLearnables,
        query: {
          sourceLanguageLocale,
          targetLanguageLocale,
          search: newSearch,
        },
      });

      void router.replace(url);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourceLanguageLocale, targetLanguageLocale],
  );

  const changeSearchWithDelay = useDebounce(changeSearch);

  useEffect(() => {
    // Reset search input when data is updated outside of UI functions (e.g., via browser back button).
    // isLoading set to false indicates that the UI code didn't initiate the update.
    /* istanbul ignore next: tested manually */
    if (searchRef.current && searchRef.current.value !== search && !isLoading) {
      searchRef.current.value = search;
    }

    setIsLoading(false);

    // We listen only for learnables updates and other variables are a snapshot of the app state at that moment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnables]);

  useEffect(() => {
    // If there is a previous search value, it indicates that the search was initiated from the UI
    if (search && previousSearchValueRef.current !== null) {
      window.dataLayer.push(eventPhrasebookPageSearch);
    }
  }, [search, eventPhrasebookPageSearch]);

  useEffect(() => {
    const search = searchRef.current;

    search?.addEventListener('input', changeSearchWithDelay);

    return () => {
      search?.removeEventListener('input', changeSearchWithDelay);
    };
  }, [changeSearchWithDelay]);

  const generateHref = useCallback(
    (pageNumber: number) =>
      getHref({
        pathname: ROUTES.phrasebook.allLearnables,
        query: {
          sourceLanguageLocale,
          targetLanguageLocale,
          page: pageNumber,
          search,
        },
      }),
    [search, sourceLanguageLocale, targetLanguageLocale],
  );

  return (
    <>
      <PhrasebookPageHeader
        flagUrl={targetLanguageImage}
        title={intl.formatMessage(targetMsgs.phrasebook)}
        description={
          <>
            {intl.formatMessage(targetMsgs.description1)}{' '}
            {intl.formatMessage(msgs.description2, {
              bold: (...chunks: ReactNode[]) => <strong>{chunks}</strong>,
            })}
          </>
        }
      />
      <S.Content
        $align="stretch"
        $spacing={isMobile ? 's2' : 's8'}
        $paddingY={isMobile ? 's0' : 's4'}
      >
        <S.SearchWrapper>
          <Search
            ref={searchRef}
            defaultValue={search}
            placeholder={intl.formatMessage(msgs.typeIn, {
              sourceLanguage: sourceLanguageName,
              targetLanguage: targetLanguageName,
            })}
          />
          {learnables?.length !== 0 && (
            <DictionaryHeader
              leftTitle={targetLanguageName}
              leftIconUrl={targetLanguageImage}
              rightTitle={sourceLanguageName}
              rightIconUrl={sourceLanguageImage}
            />
          )}
        </S.SearchWrapper>
        <DictionaryList
          list={learnables}
          isLoading={!learnables || isLoading}
          currentPage={page}
          searchData={searchData}
        />
        {learnables?.length === 0 && (
          <S.NoResults>
            <S.NoResultsTitle>{intl.formatMessage(msgs.noResults)}</S.NoResultsTitle>
            <S.NoResultsDescription>
              {intl.formatMessage(msgs.noResultsDescription)}
            </S.NoResultsDescription>
          </S.NoResults>
        )}
        <Pagination
          currentPage={page}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          siblingCount={1}
          onPageChange={changePage}
          generateHref={generateHref}
        />
      </S.Content>
    </>
  );
};
